import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useIsDesigner } from "@/hooks/useIsDesigner";

interface DesignerGuardProps {
  children: React.ReactNode;
}

/**
 * Wraps designer-only routes (dashboard, workspace, project, instructor, etc.).
 * Learners (enrolled-only users) are redirected to /learn.
 */
export default function DesignerGuard({ children }: DesignerGuardProps) {
  const navigate = useNavigate();
  const { session, isLoading: authLoading } = useAuth();
  const { isDesigner, isLoading: designerLoading } = useIsDesigner(session?.user?.id);

  useEffect(() => {
    if (authLoading || designerLoading) return;
    if (!session) {
      navigate("/auth", { replace: true });
      return;
    }
    if (!isDesigner) {
      navigate("/learn", { replace: true });
    }
  }, [session, isDesigner, authLoading, designerLoading, navigate]);

  if (authLoading || designerLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!session || !isDesigner) {
    return null;
  }

  return <>{children}</>;
}
